import { TagViewModel } from "client/Api/Tags/tags";
import { UserIdentityModel, UserPronounKind } from "client/Api/UserProfile/service";
import {
	HubLinkedDocumentViewModel,
	HubItemMediaPreviewViewModel,
	HubItemViewerListItemModel,
	ReactionCounterModel,
	ReactionKind,
	ReactionUserModel
} from "client/Api/Hub/common";
import {
	HubCommentOptionsModel,
	HubItemLayoutDetailsViewModel,
	HubItemMediaDetailsViewModel,
	RegisterViewModel
} from "client/Api/Hub/common.details";
import { SortDirection } from "client/Api/common";
import { LocationType, TicketType } from "client/Api/EventsManagement/eventsManagement";
import { environmentHttpClient } from "client/Api/httpClient";

export type PreviewItemCategoryModel = {
	id: number;
	name: string;
};

export type PreviewItemUserModel = {
	id: number;
	name: string;
};

export enum EventLocationType {
	Unknown = 0,
	Online = 1,
	Physical = 2,
	Hybrid = 3
};

export type EventPreviewModel = {
	id: number;
	startDate: Date;
	endDate: Date;
	title: string;
	descriptionPreview: string;
	category: PreviewItemCategoryModel | null;
	author: PreviewItemUserModel | null;
	locationType: EventLocationType;
	location: string | null;
	media: HubItemMediaPreviewViewModel;
	attendeeCount: number | null;
	isNew: boolean;
	reactionCounters: readonly ReactionCounterModel[];
	commentsCount: number;
	areCommentsEnabled: boolean;
};

export const enum EventSortProperty {
	StartDate = 0
};

export type EventOrderModel = {
	property: EventSortProperty;
	direction: SortDirection;
};

export enum EventDateFilterType {
	Upcoming = 0,
	Past = 1
}

export enum EventDateSortDirection {
	NearToFar = 0,
	FarToNear = 1
}

export type EventPagingInformationModel = {
	id: number;
	startDate: Date;
};

export enum EventAttendeeKind {
	Online = 1,
	Physical = 2
}

export type EventListQueryParameters = {
	categoryId?: number | null;
	authorId?: number | null;
	tagId?: number | null;
	searchTerm?: string | null;
	sortDirection?: EventDateSortDirection | null;
	filterType?: EventDateFilterType | null;
	pagingInformation?: EventPagingInformationModel | null;
	amount?: number | null;
	isNew?: boolean | null;
};

export function list(
	parameters: EventListQueryParameters,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<EventPreviewModel>> {
	return environmentHttpClient.get<LazyLoadPageResult<EventPreviewModel>>(
		"UiApi/Hub/Events",
		parameters,
		{ signal }
	);
}

export type AttendeePreviewModel = {
	kind: EventAttendeeKind;
	user: UserIdentityModel;
};

export type AttendeeCounter = {
	kind: EventAttendeeKind;
	count: number;
};

export type EventDetailsViewModel = {
	id: number;
	title: string;
	layout: HubItemLayoutDetailsViewModel;
	media: HubItemMediaDetailsViewModel;
	startDate: Date;
	author: UserIdentityModel | null;
	categoryId: number;
	tags: readonly TagViewModel[];
	isEditable: boolean;
	previewAttendees: readonly AttendeePreviewModel[];
	lastModifiedUser: UserIdentityModel | null;
	lastModifiedDate: Date;
	endDate: Date;
	publicationDate: Date;
	signUpDeadline: Date;
	locationType: LocationType;
	location: string | null;
	externalUrl: string | null;
	onlineEventUrl: string | null;
	ticketType: TicketType;
	availableSpots: number | null;
	attendeeCounts: readonly AttendeeCounter[];
	attendeeKind: EventAttendeeKind | null;
	isBeforeSignUpDate: boolean;
	isBeforeEventStartDate: boolean;
	linkedDocuments: readonly HubLinkedDocumentViewModel[] | null;
	isAttendanceEnabled: boolean;
	isNew: boolean;
	areCommentsEnabled: boolean;
};

export type EventDetailsPageViewModel = {
	event: EventDetailsViewModel;
	commentOptions: HubCommentOptionsModel;
};

export function get(
	id: number,
	signal?: AbortSignal
): Promise<EventDetailsPageViewModel> {
	return environmentHttpClient.get<EventDetailsPageViewModel>(
		`UiApi/Hub/Events/${id}`,
		undefined,
		{ signal }
	);
}

export enum AttendanceErrorStatus {
	NoSpotsAvailable = 1,
	SignUpDeadlineHasPassed = 2,
	InvalidOperationForUser = 3,
	EventHasStarted = 4
}

export type UpdateEventAttendanceErrorModel = {
	status: AttendanceErrorStatus;
};

export type EventAttendeesInformationModel = {
	attendeeKind: EventAttendeeKind | null;
	isBeforeSignUpDeadline: boolean;
	isBeforeEventStartDate: boolean;
	attendeeCounts: readonly AttendeeCounter[];
	maxAttendees: number;
	signUpDeadline: Date | null;
	previewAttendees: readonly AttendeePreviewModel[];
};

export type SuccessfulUpdateEventAttendanceResultModel = {
	attendeesInformation: EventAttendeesInformationModel;
	updateEventInCalendarResult: UpdateEventInCalendarResult;
};

export function attendEvent(
	eventId: number,
	kind: EventAttendeeKind,
	signal?: AbortSignal
): Promise<SuccessfulUpdateEventAttendanceResultModel> {
	return environmentHttpClient.post<SuccessfulUpdateEventAttendanceResultModel>(
		`UiApi/Hub/Events/${eventId}/Attendance`,
		{ kind },
		{ signal }
	);
}

export function dontAttendEvent(
	eventId: number,
	signal?: AbortSignal
): Promise<SuccessfulUpdateEventAttendanceResultModel> {
	return environmentHttpClient.delete<SuccessfulUpdateEventAttendanceResultModel>(
		`UiApi/Hub/Events/${eventId}/Attendance`,
		undefined,
		{ signal }
	);
}

export function getAttendees(
	eventId: number,
	kind: EventAttendeeKind | undefined,
	pageSize: number,
	skipAmount: number,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<AttendeePreviewModel>> {
	return environmentHttpClient.get<LazyLoadPageResult<AttendeePreviewModel>>(
		`UiApi/Hub/Events/${eventId}/Attendees`,
		{
			kind,
			pageSize,
			skipAmount
		},
		{signal}
	);
}

export type AttendeeExportModel = {
	userId: number;
	firstName: string;
	lastName: string;
	primaryEmail: string | null;
	phoneNumber: string | null;
	jobTitle: string | null;
	officeLocation: string | null;
	attendeeKind: EventAttendeeKind;
	pronouns: UserPronounKind;
};

export function listAttendeesForExport(
	id: number,
	signal?: AbortSignal
): Promise<readonly AttendeeExportModel[]> {
	return environmentHttpClient.get<readonly AttendeeExportModel[]>(
		`UiApi/Hub/Events/${id}/Attendees/Export`,
		undefined,
		{ signal }
	);
}

type OtherEventsData = {
	id: number;
	pagingInformation: EventPagingInformationModel;
	categoryId: number | null;
	authorId: number | null;
	tagId: number | null;
	searchTerm: string | null;
	filterType: EventDateFilterType;
	sortDirection: EventDateSortDirection | null;
	isNew: boolean | null;
};

export function getOtherEvents(
	data: OtherEventsData,
	signal?: AbortSignal
): Promise<readonly EventPreviewModel[]> {
	const {
		id,
		pagingInformation,
		categoryId,
		authorId,
		tagId,
		searchTerm,
		filterType,
		sortDirection,
		isNew
	} = data;

	return environmentHttpClient.get<readonly EventPreviewModel[]>(
		`UiApi/Hub/Events/${id}/Relevant`,
		{
			pagingInformation,
			categoryId,
			authorId,
			tagId,
			searchTerm,
			filterType,
			sortDirection,
			isNew
		},
		{ signal }
	);
}

export enum UpdateEventInCalendarResult {
	Unknown = 0,
	Success = 1,
	Failure = 2,
	NotUpdated = 3
};

export function registerInCalendar(
	id: number,
	signal?: AbortSignal
): Promise<UpdateEventInCalendarResult> {
	return environmentHttpClient.post<UpdateEventInCalendarResult>(
		`UiApi/Hub/Events/${id}/RegisterCalendar`,
		undefined,
		{ signal }
	);
}

export enum RemoveEventFromCalendarResult {
	Unknown = 0,
	Success = 1,
	Failure = 2
};

export function removeFromCalendar(
	id: number,
	signal?: AbortSignal
): Promise<RemoveEventFromCalendarResult> {
	return environmentHttpClient.delete<RemoveEventFromCalendarResult>(
		`UiApi/Hub/Events/${id}/RemoveFromCalendar`,
		undefined,
		{ signal }
	);
};

export function registerView(
	id: number,
	model: RegisterViewModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.post<void>(
		`UiApi/Hub/Events/${id}/Views`,
		model,
		{ signal }
	);
}

export type EventStatisticsModel = {
	viewsCount: number;
	eventId: number;
	reactionCounters: readonly ReactionCounterModel[];
	currentUserReaction: ReactionKind;
	commentsCount: OptionalModel<number>;
	isCurrentUserFollowingComments: boolean;
};

export function getStatistics(id: number, signal?: AbortSignal): Promise<EventStatisticsModel> {
	return environmentHttpClient.get<EventStatisticsModel>(
		`UiApi/Hub/Events/${id}/Statistics`,
		undefined,
		{ signal }
	);
}

export function listViewers(
	id: number,
	skip: number,
	take: number,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<HubItemViewerListItemModel>> {
	return environmentHttpClient.get<LazyLoadPageResult<HubItemViewerListItemModel>>(
		`UiApi/Hub/Events/${id}/Viewers`,
		{
			skip,
			take
		},
		{ signal }
	);
}

export function doesUserHaveAccess(
	eventId: number,
	userId: number,
	signal?: AbortSignal
): Promise<boolean> {
	return environmentHttpClient.get<boolean>(
		`UiApi/Hub/Events/${eventId}/DoesUserHaveAccess`,
		{
			userId
		},
		{ signal }
	);
}

export function findUsers(
	eventId: number,
	searchTerm: string,
	amount: number,
	signal?: AbortSignal
): Promise<readonly UserIdentityModel[]> {
	return environmentHttpClient.get<readonly UserIdentityModel[]>(
		`UiApi/Hub/Events/${eventId}/Users`,
		{
			searchTerm,
			amount
		},
		{ signal }
	);
}

export function registerReaction(
	id: number,
	kind: ReactionKind,
	signal?: AbortSignal
): Promise<EventStatisticsModel> {
	return environmentHttpClient.post<EventStatisticsModel>(
		`UiApi/Hub/Events/${id}/React`,
		kind,
		{ signal }
	);
}

export function updateReaction(
	id: number,
	kind: ReactionKind,
	signal?: AbortSignal
): Promise<EventStatisticsModel> {
	return environmentHttpClient.put<EventStatisticsModel>(
		`UiApi/Hub/Events/${id}/React`,
		kind,
		{ signal }
	);
}

export function unregisterReaction(
	id: number,
	signal?: AbortSignal
): Promise<EventStatisticsModel> {
	return environmentHttpClient.delete<EventStatisticsModel>(
		`UiApi/Hub/Events/${id}/React`,
		undefined,
		{ signal }
	);
}

export function registerFollowComments(
	id: number,
	signal?: AbortSignal
): Promise<EventStatisticsModel> {
	return environmentHttpClient.post<EventStatisticsModel>(
		`UiApi/Hub/Events/${id}/FollowComments`,
		undefined,
		{ signal }
	);
}

export function unregisterFollowComments(
	id: number,
	signal?: AbortSignal
): Promise<EventStatisticsModel> {
	return environmentHttpClient.delete<EventStatisticsModel>(
		`UiApi/Hub/Events/${id}/FollowComments`,
		undefined,
		{ signal }
	);
}

export function getReactionCounts(
	eventId: number,
	signal?: AbortSignal
): Promise<readonly ReactionCounterModel[]> {
	return environmentHttpClient.get<readonly ReactionCounterModel[]>(
		`UiApi/Hub/Events/${eventId}/ReactionCounts`,
		undefined,
		{ signal }
	);
}

export function getReactions(
	eventId: number,
	pageSize: number,
	skip: number,
	kind: ReactionKind | undefined,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<ReactionUserModel>> {
	return environmentHttpClient.get<LazyLoadPageResult<ReactionUserModel>>(
		`UiApi/Hub/Events/${eventId}/Reactions`,
		{
			kind,
			pageSize,
			skip
		},
		{ signal }
	);
}